import type { CSSProperties, KeyboardEvent } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  usePublicLandingDemo,
  usePublicOnboardingCatalog,
} from "@/api/queries/publicOnboarding";
import { usePlatformOperations } from "@/api/queries/processes";
import type { PlatformOperation } from "@/api/types";
import { OperationIcon } from "@/components/cart/OperationIcon";
import { getOperationTone, operationToneColor } from "@/lib/operationTone";
import { PlatformSamplePrice } from "./PlatformSamplePrice";

/** Other operations stacked on the „more" tile. */
const STACKED = 3;

/** A tile's shading — the three tones of its CATEGORY colour (KAN-1962). */
function toneStyle(op: PlatformOperation | undefined): CSSProperties {
  const tone = getOperationTone(operationToneColor(op));
  return {
    "--cb": tone.bg,
    "--ci": tone.ink,
    "--cd": tone.border,
  } as CSSProperties;
}

/**
 * KAN-1945 — the sample offers the machines the platform admin ticked for it
 * (``in_demo``), not the whole catalog: a visitor does not know what a
 * „Prusa XL (5 hlav)" is, and a shop offers only the machines it has. Nothing
 * ticked = the operation's first machine, the one the sample started on.
 */
export function demoMachines<M extends { in_demo?: boolean }>(machines: M[]): M[] {
  const picked = machines.filter((m) => m.in_demo);
  return picked.length > 0 ? picked : machines.slice(0, 1);
}

/**
 * KAN-1962 — the hero's configurator card. Which processes it offers is the
 * platform admin's pick (at most two): none = no card, one = no switcher,
 * two = a switcher with a third tile pointing at everything else.
 */
export function PlatformDemoCard(): JSX.Element | null {
  const { t, i18n } = useTranslation("common");
  const lang = i18n.language;
  const catalog = usePublicOnboardingCatalog(lang);
  const demo = usePublicLandingDemo();
  const platformOps = usePlatformOperations();
  const [slug, setSlug] = useState("");

  const offered = catalog.data?.operations ?? [];
  const featured = (demo.data?.process_slugs ?? [])
    .map((s) => offered.find((o) => o.slug === s))
    .filter((o): o is NonNullable<typeof o> => Boolean(o));
  if (catalog.isPending || demo.isPending) {
    return (
      <div className="sw" data-testid="platform-demo">
        <p className="sw-card">{t("platformSample.loading")}</p>
      </div>
    );
  }
  if (featured.length === 0) return null;

  const operation = featured.find((o) => o.slug === slug) ?? featured[0];
  const meta = (s: string) =>
    platformOps.data?.find((o) => (o.name ?? o.slug) === s);
  const others = (platformOps.data ?? []).filter(
    (o) => !featured.some((f) => f.slug === (o.name ?? o.slug)),
  );
  const onKey = (e: KeyboardEvent) => {
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
    const i = featured.indexOf(operation);
    const next = featured[(i + 1) % featured.length];
    setSlug(next.slug);
    document.getElementById(`platform-demo-tab-${next.slug}`)?.focus();
  };

  return (
    <div className="sw" data-testid="platform-demo">
      {featured.length > 1 && (
        <>
          <div className="sw-cap">{t("platformSample.pickProcess")}</div>
          <div className="sw-ops">
            <div role="tablist" className="contents" onKeyDown={onKey}>
              {featured.map((o) => {
                const m = meta(o.slug);
                const selected = o.slug === operation.slug;
                return (
                  <button
                    key={o.slug}
                    id={`platform-demo-tab-${o.slug}`}
                    type="button"
                    role="tab"
                    aria-selected={selected}
                    tabIndex={selected ? 0 : -1}
                    className="sw-op"
                    style={toneStyle(m)}
                    onClick={() => setSlug(o.slug)}
                    data-testid={`platform-demo-op-${o.slug}`}
                  >
                    <span aria-hidden="true" className="sw-chk">
                      ✓
                    </span>
                    <i aria-hidden="true">
                      <OperationIcon
                        iconKey={m?.icon_key}
                        categoryColor={operationToneColor(m)}
                        slug={o.slug}
                        displayName={o.display_name}
                        size={34}
                      />
                    </i>
                    <b>{o.display_name}</b>
                    {o.materials.length > 0 && (
                      <span className="sw-meta">
                        {o.materials
                          .slice(0, 4)
                          .map((x) => x.display_name)
                          .join(" · ")}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            {others.length > 0 && (
              <a
                href="#operace"
                className="sw-more"
                data-testid="platform-demo-more"
              >
                <span aria-hidden="true" className="sw-stack">
                  {others.slice(0, STACKED).map((o) => (
                    <span key={o.slug} style={toneStyle(o)}>
                      <OperationIcon
                        iconKey={o.icon_key}
                        categoryColor={operationToneColor(o)}
                        slug={o.slug}
                        displayName={o.display_name}
                        size={18}
                      />
                    </span>
                  ))}
                  {others.length > STACKED && (
                    <span className="sw-n">+{others.length - STACKED}</span>
                  )}
                </span>
                <b>{t("platformSample.moreOps")}</b>
                <small>
                  {others
                    .slice(0, STACKED)
                    .map((o) => o.display_name)
                    .join(", ")}
                  {others.length > STACKED ? "…" : ""}
                </small>
                <em>{t("platformSample.moreOpsCta")}</em>
              </a>
            )}
          </div>
        </>
      )}
      <div
        role={featured.length > 1 ? "tabpanel" : undefined}
        aria-labelledby={
          featured.length > 1
            ? `platform-demo-tab-${operation.slug}`
            : undefined
        }
      >
        <PlatformSamplePrice
          key={operation.slug}
          operation={operation}
          machines={demoMachines(operation.machines).map((m) => ({
            machineKey: m.machine_key,
            addonKeys: [],
          }))}
          showcase
          materialCodes={operation.materials.map((m) => m.code)}
          lang={lang}
          collapsible
        />
      </div>
      {/* Once per page, not per card — the try-it page says it in its heading. */}
      <p className="sw-fine">{t("platformTryout.preview.sub")}</p>
    </div>
  );
}
